import { Component, effect, inject, input } from '@angular/core';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { VisualBaseComponent } from '../visual-base.component';
import { QuarterlyGroup } from '../../services/metric.service';
import { Peer, StateService } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';

@Component({
  selector: 'app-metric-quarterly-group',
  standalone: true,
  imports: [NgxEchartsDirective],
  templateUrl: 'metric-quarterly-group.component.html',
  styleUrl: './metric-quarterly-group.component.scss',
  providers: [provideEcharts()]
})
export class MetricQuarterlyGroupComponent extends VisualBaseComponent {
  metric = input<QuarterlyGroup | undefined>();

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly peerName = inject(StateService).peerName;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly date = inject(StateService).date;

  override initOptions = { height: 150 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 10,
      top: 30,
      bottom: 10,
      containLabel: true
    },
    legend: {
      show: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      },
      data: []
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b'
      },
      splitLine: {
        lineStyle: {
          color: '#ff0000'
        }
      }
    },
    axisLabel: {
      color: '#002f8e'
    },
    series: [
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 0,
        data: []
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 0,
        data: []
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => value != null ? VisualService.getValueDisplay(Number(value), this.metric()?.displayType) : '--'
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
        formatter: (value: number) => this.getYAxisDisplay(this.metric()?.displayType, Number(value))
      },
      splitLine: {
        lineStyle: {
          color: '#ccc'
        }
      }
    }
  };
  override loadingColor = '#ebebeb';

  constructor() {
    super();

    effect(() => {
      const peer = this.peer();
      const group = this.filteredGroup();
      const date = this.date();
      const metric = this.metric();
      if (this.chartInit() && peer && group && date && metric) {
        this.updateVisual(peer, date, metric);
      }
    });
  }

  public updateVisual(peer: Peer, date: Date, metric: QuarterlyGroup) {
    this.metricService.getMetricData(this.institution().iid, metric.metricId, peer)?.subscribe(data => {
      const dateRange = { startDate: new Date(new Date(date).setFullYear(new Date(date).getFullYear() - 5)), endDate: date };
      const dataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, data, dateRange);

      if (dataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
      }
      else {
        this.updateOptions = {
          xAxis: {
            data: dataSeries.dateSeries
          },
          series: [
            {
              name: this.institution()?.name,
              data: dataSeries.institutionSeries,
              color: this.colors[0],
              symbol: 'line',
              symbolSize: 0,
            },
            {
              name: this.peerName(),
              data: dataSeries.peerSeries,
              color: this.colors[1],
              symbol: 'line',
              symbolSize: 0,
            }
          ]
        };
      }
      this.echartsInstance?.hideLoading();
    });
  }

  private getYAxisDisplay(displayType: string | undefined = '', value: number): string {
    const formatSymbol = (displayType == 'USD') ? 'M' : (displayType == 'PCT') ? '%' : '';
    return (displayType == 'USD' ? value.toLocaleString() : value.toFixed(1)) + formatSymbol;
  }
}
